import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  FilterPopover as StyledFilterPopover,
  FilterAccordion,
  FilterAccordionSummary,
  FilterAccordionDetails,
  StyledInputBase,
  PriceButton,
  ActionButton,
} from './styles';
import { formatThaiCurrency } from './utils';
import { useTranslation } from 'next-i18next';

interface FilterPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  priceMin: number;
  priceMax: number;
  minInputValue: string;
  maxInputValue: string;
  priceRangesMin: number[];
  priceRangesMax: number[];
  onMinInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMinPriceSelect: (price: number) => void;
  onMaxPriceSelect: (price: number) => void;
  onApply: () => void;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  priceMin,
  priceMax,
  minInputValue,
  maxInputValue,
  priceRangesMin,
  priceRangesMax,
  onMinInputChange,
  onMaxInputChange,
  onMinPriceSelect,
  onMaxPriceSelect,
  onApply,
}) => {
  const { t } = useTranslation('common');

  return (
    <StyledFilterPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Box sx={{ maxHeight: '540px', overflowY: 'auto', paddingBottom: '70px' }}>
        <FilterAccordion defaultExpanded>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('price')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <StyledInputBase
                  placeholder={t('min_price')}
                  value={minInputValue}
                  onChange={onMinInputChange}
                />
                <Box mt={1}>
                  {priceRangesMin.map((price) => (
                    <PriceButton 
                      key={`min-${price}`}
                      onClick={() => onMinPriceSelect(price)}
                    >
                      {price === 0 ? t('select') : formatThaiCurrency(price) + ' ' + t('THB')}
                    </PriceButton>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={6}>
                <StyledInputBase
                  placeholder={t('max_price')}
                  value={maxInputValue}
                  onChange={onMaxInputChange}
                />
                <Box mt={1}>
                  {priceRangesMax.map((price) => (
                    <PriceButton 
                      key={`max-${price}`}
                      onClick={() => onMaxPriceSelect(price)}
                    >
                      {formatThaiCurrency(price) + ' ' + t('THB')}
                    </PriceButton>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('bedroom')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Bedrooms content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('bathroom')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Bathrooms content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('useable_area')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Area content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('category')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Structure content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('Status')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Status content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('floor-unit')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Floor content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('View')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* View content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('Direction')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Direction content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">ประเทศไทย</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Thailand content */}
          </FilterAccordionDetails>
        </FilterAccordion>
        <Divider />
        
        <FilterAccordion>
          <FilterAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="medium">{t('Created date')}</Typography>
          </FilterAccordionSummary>
          <FilterAccordionDetails>
            {/* Posted date content */}
          </FilterAccordionDetails>
        </FilterAccordion>
      </Box>
      
      <Box sx={{
        position: 'absolute', 
        bottom: 0, 
        left: 0, 
        right: 0,
        padding: '16px',
        borderTop: '1px solid #E0E0E0',
        backgroundColor: '#ffffff',
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2
      }}>
        <ActionButton 
          variant="outlined"
          onClick={onClose}
          sx={{ 
            borderRadius: '25px',
            flex: 1,
            color: '#666666',
            borderColor: '#D3D3D3'
          }}
        >
          {t('reset')}
        </ActionButton>
        <ActionButton 
          variant="contained"
          onClick={onApply}
          sx={{ 
            borderRadius: '25px',
            flex: 1,
            backgroundColor: '#F4762A',
            '&:hover': {
              backgroundColor: '#D25E20',
            }
          }}
        >
          {t('confirm')}
        </ActionButton>
      </Box>
    </StyledFilterPopover>
  );
};

export default FilterPopover; 